import { Alert, Descriptions, Table, Typography } from 'antd'

import { useClustersQuery } from '@/hooks/useClusters'

import type { CatalogImage } from '../api'

export function ImageDetails({ image }: { image: CatalogImage }) {
  const clusters = useClustersQuery()
  const source = image.source
  const names = new Map(
    clusters.data?.map((c) => [c.id.replaceAll('-', ''), c.name]),
  )
  return (
    <>
      <Descriptions
        size="small"
        column={{ xs: 1, md: 2 }}
        items={[
          {
            key: 'id',
            label: 'Catalog ID',
            children: <Typography.Text copyable>{image.id}</Typography.Text>,
          },
          {
            key: 'url',
            label: 'Source URL',
            children: (
              <Typography.Text style={{ overflowWrap: 'anywhere' }} copyable>
                {source.downloadUrl}
              </Typography.Text>
            ),
          },
          {
            key: 'username',
            label: 'Guest username',
            children: source.username,
          },
          { key: 'boot', label: 'Boot mode', children: source.bootMode },
          ...(source.kind === 'IMAGE'
            ? [
                {
                  key: 'size',
                  label: 'Virtual disk size (bytes)',
                  children: source.virtualSize.toLocaleString(),
                },
                {
                  key: 'sha',
                  label: 'SHA-256',
                  children: source.sha256 || 'Not specified',
                },
              ]
            : []),
        ]}
      />
      {source.kind === 'IMAGE' && (
        <>
          <Typography.Paragraph type="secondary">
            Last observations, not a guarantee of current readiness. Publishing
            performs a fresh server check.
          </Typography.Paragraph>
          {clusters.error && (
            <Alert
              type="warning"
              message="Cluster names unavailable; showing IDs."
            />
          )}
          <Table
            size="small"
            pagination={false}
            rowKey="clusterId"
            dataSource={image.preparations}
            locale={{
              emptyText:
                'No cluster observations yet. Preparation starts in the background.',
            }}
            scroll={{ x: 650 }}
            columns={[
              {
                title: 'Cluster',
                dataIndex: 'clusterId',
                render: (id: string) => names.get(id.replaceAll('-', '')) ?? id,
              },
              {
                title: 'Last phase',
                dataIndex: 'phase',
                render: (phase: string | null) => phase ?? 'Not observed',
              },
              {
                title: 'Observed at',
                dataIndex: 'observedAt',
                render: (at: string | null) =>
                  at ? new Date(at).toLocaleString() : 'Never',
              },
              {
                title: 'Last error',
                key: 'error',
                render: (_, p) =>
                  [p.errorDescription ?? p.error, p.errorHttpStatus]
                    .filter((v) => v != null)
                    .join(' · ') || '—',
              },
            ]}
          />
        </>
      )}
    </>
  )
}
